import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, BookOpen, ChevronDown, Calendar, Book, Menu, X, Shield } from 'lucide-react';
import { ConsultationBooking } from './ConsultationBooking';
import { motion, AnimatePresence } from 'framer-motion';

export const Navigation = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showConsultation, setShowConsultation] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const calculatorTypes = [
    { path: "/", name: "Budget Calculator" },
    { path: "/take-home", name: "Take Home Calculator" },
    { path: "/withholding", name: "Withholding Calculator" },
    { path: "/retirement", name: "Retirement Calculator" },
    { path: "/loan", name: "Loan Amortization" },
    { path: "/student-loan", name: "Student Loan Calculator" },
    { path: "/investment", name: "Investment Calculator" },
    { path: "/net-worth", name: "Net Worth Calculator" },
    { path: "/military", name: "Military Calculator" },
    { path: "/documents", name: "Document Analysis" }
  ];

  return (
    <nav className="relative z-50 mb-8">
      {/* Desktop Navigation */}
      <div className="hidden md:flex gap-4">
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="px-4 py-2 flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition-colors"
          >
            <Calculator className="w-4 h-4" />
            Calculators
            <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-64 glass-card border border-emerald-500/20 rounded-lg overflow-hidden z-50">
              {calculatorTypes.map((calc) => (
                <Link
                  key={calc.path}
                  to={calc.path}
                  className="px-4 py-3 flex items-center gap-2 hover:bg-emerald-500/10 transition-colors text-slate-300 hover:text-emerald-500"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  {calc.name}
                </Link>
              ))}
            </div>
          )}
        </div>

        <Link
          to="/blog"
          className="px-4 py-2 flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition-colors"
        >
          <Book className="w-4 h-4" />
          Blog
        </Link>

        <Link
          to="/savings-blog"
          className="px-4 py-2 flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          Savings Guide
        </Link>

        <button
          onClick={() => setShowConsultation(!showConsultation)}
          className="px-4 py-2 flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition-colors"
        >
          <Calendar className="w-4 h-4" />
          Book Consultation
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 text-emerald-500 hover:text-emerald-400 transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-full left-0 right-0 glass-card border border-emerald-500/20 rounded-lg mt-2 overflow-hidden"
            >
              <div className="p-4 space-y-2">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full px-4 py-3 flex items-center justify-between text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Calculators
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      {calculatorTypes.map((calc) => (
                        <Link
                          key={calc.path}
                          to={calc.path}
                          className="block px-8 py-2 text-slate-300 hover:text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {calc.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <Link
                  to="/blog"
                  className="block px-4 py-3 text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <Book className="w-4 h-4" />
                    Blog
                  </span>
                </Link>

                <Link
                  to="/savings-blog"
                  className="block px-4 py-3 text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Savings Guide
                  </span>
                </Link>

                <button
                  onClick={()=> {
                    setShowConsultation(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors text-left"
                >
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Book Consultation
                  </span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showConsultation && (
        <ConsultationBooking onClose={() => setShowConsultation(false)} />
      )}
    </nav>
  );
};